import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReload = () => {
    // Clear session storage to force re-authentication
    sessionStorage.clear();
    window.location.href = "/";
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      const isAuthError = this.state.error?.message?.includes("AADSTS") ||
        this.state.error?.message?.includes("token") ||
        this.state.error?.name === "ServerError" ||
        this.state.error?.name === "InteractionRequiredAuthError";

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="#f5f5f5"
        >
          <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: "center" }}>
            <Typography variant="h5" gutterBottom color="error">
              {isAuthError ? "Authentication Error" : "Something went wrong"}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              {isAuthError
                ? "Your session has expired or there was an authentication problem. Please sign in again."
                : "An unexpected error occurred. Please try again."}
            </Typography>
            {this.state.error && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 2, fontFamily: "monospace" }}
              >
                {this.state.error.message?.substring(0, 200)}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button variant="outlined" onClick={this.handleRetry}>
                Try Again
              </Button>
              <Button variant="contained" color="primary" onClick={this.handleReload}>
                {isAuthError ? "Sign In Again" : "Reload Page"}
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
